import { House, Library, LogIn } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { LinkAction } from "../components/Screen.js";

import { useSession } from "../hooks/useSession.js";
import {
  API_ERROR,
  HttpError,
  fetchHall,
  type BoardLine,
  type HallView,
} from "../services/http.js";

/**
 * The hall of fame — `/hall`, phase 16j-2.
 *
 * # Several small boards, not one ranking
 *
 * Decided with the maintainer: for a friend group this is the difference between
 * a page people revisit and a page that settles into a fixed order and dies.
 * More people get to be top of something, and it stops "whoever plays most
 * evenings" from being permanently first at everything.
 *
 * # ⚠️ Counts, never rates
 *
 * `CareerPage` is explicit about this and the recap agrees: a rate's denominator
 * would be *rounds you were scored in*, which in Classic is roughly your share
 * of the turns rather than the length of the evening. A percentage here would
 * quietly contradict every other screen.
 *
 * # "No games yet" rather than four empty boards
 *
 * Also decided: **no seeded invented players** to make the installation look
 * busy. A fake name is read as a real person, and the first player who cannot
 * find themselves among them learns the numbers are not real. An honest empty
 * state reads as *new*; four empty headings read as *broken*.
 *
 * # Account holders only
 *
 * Guests play without accounts (ADR 0007), so a board cannot name somebody who
 * never signed up for one. Enforced server-side, not here.
 */
export function HallPage() {
  const { t } = useTranslation();
  const { account } = useSession();
  const [hall, setHall] = useState<HallView | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!account) {
      return;
    }
    let cancelled = false;
    void fetchHall().then(
      (loaded) => {
        if (!cancelled) {
          setHall(loaded);
        }
      },
      (cause: unknown) => {
        if (cancelled) {
          return;
        }
        // A session that expired between the two requests is not an error to
        // show — `useSession` will have the same answer on the next render.
        if (cause instanceof HttpError && cause.code === API_ERROR.unauthenticated) {
          return;
        }
        setError(cause instanceof Error ? cause.message : t("career.loadFailed"));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [account, t]);

  // Three states, not two: `undefined` is still asking, and a screen that
  // flashed "sign in" at somebody signed in is worse than a slower one.
  if (account === undefined) {
    return (
      <Frame>
        <p className="text-sm text-white/50">{t("hall.loading")}</p>
      </Frame>
    );
  }

  if (account === null) {
    return (
      <Frame>
        <p className="text-sm text-white/60">{t("hall.needsAccount")}</p>
        <LinkAction to="/login" icon={LogIn} accent>
          {t("login.title")}
        </LinkAction>
      </Frame>
    );
  }

  if (error) {
    return (
      <Frame>
        <p role="alert" className="text-sm text-wrong-500">
          {error}
        </p>
      </Frame>
    );
  }

  if (!hall) {
    return (
      <Frame>
        <p className="text-sm text-white/50">{t("hall.loading")}</p>
      </Frame>
    );
  }

  if (hall.empty) {
    return (
      <Frame>
        <h2 className="text-xl font-semibold">{t("hall.emptyTitle")}</h2>
        <p className="mt-1 text-sm text-white/60">{t("hall.emptyLead")}</p>
        <LinkAction to="/" icon={House} accent>
          {t("hall.startOne")}
        </LinkAction>
      </Frame>
    );
  }

  return (
    <Frame>
      <div className="grid gap-6 sm:grid-cols-2">
        <Board label={t("hall.gamesWon")} lines={hall.most_games_won} />
        <Board label={t("hall.placements")} lines={hall.most_placements} />
        <Board label={t("hall.challengesWon")} lines={hall.most_challenges_won} />
        <Board label={t("hall.steals")} lines={hall.most_steals} />
      </div>

      {/* Phase 16j-3, linked from here rather than left unreachable — a page
          nothing links to is a page nobody finds. */}
      <LinkAction to="/archive" icon={Library}>
        {t("archive.link")}
      </LinkAction>
    </Frame>
  );
}

/** One board. The four differ only in what they count, which is the point. */
function Board({ label, lines }: { label: string; lines: readonly BoardLine[] }) {
  const { t } = useTranslation();

  return (
    <section aria-label={label} data-testid="hall-board">
      <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-white/50">
        {label}
      </h2>
      {lines.length === 0 ? (
        // A board nobody is on yet says so, rather than rendering as a heading
        // with nothing under it.
        <p className="text-xs text-white/40">{t("hall.boardEmpty")}</p>
      ) : (
        <ol className="flex flex-col gap-1">
          {lines.map((line, index) => (
            <li
              key={line.profile_id}
              className="flex items-baseline justify-between gap-3 text-sm"
            >
              <span className="min-w-0 truncate">
                <span className="mr-2 text-xs tabular-nums text-white/40">
                  {index + 1}
                </span>
                {line.display_name}
              </span>
              <span className="shrink-0 tabular-nums text-white/70">{line.count}</span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

/** One frame for every state, so the header does not move as the page resolves. */
function Frame({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();

  return (
    <main className="mx-auto flex min-h-full w-full max-w-3xl flex-col p-5 pb-16">
      <header className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-flash-500">
          {t("app.name")}
        </p>
        <h1 className="mt-1 text-3xl font-semibold">{t("hall.title")}</h1>
        <p className="mt-1 text-sm text-white/60">{t("hall.lead")}</p>
      </header>
      {children}
    </main>
  );
}
